import { useEffect } from "react";
import { Box, Divider } from "@mui/material";

import useIsOwn from "../../../../../../../hooks/useIsOwn";

import { ServiceRequest } from "../../../../../types/service.type";
import { useContractState } from "../../../../../hooks/useContractState";
import { useContractPayment } from "../../../../../hooks/useContractPayment";
import { useGetContractDetails } from "../../../../../api/queries/useGetContractDetails";
import { useServiceFeedbackPaginated } from "../../../../../api/queries/useServiceFeedback";
import { ServiceFeedbackCard, ServiceFeedbackCardLoader } from "../../../../../components/ServiceFeedbackCard";

import { UserDetails } from "./UserDetails";
import { ContractActions } from "./ContractActions";
import { MilestoneTimeline } from "./MilestoneTimeline";
import { ContractDetailsCard } from "./ContractDetailsCard";
import { ContractServiceDetailsCard } from "./ContractServiceDetailsCard";

const ContractDetails = ({
  serviceRequest,
  handleSetIsContractPaid,
}: {
  serviceRequest: ServiceRequest;
  handleSetIsContractPaid: (isContractPaid: boolean) => void;
}) => {
  const { data: contractDetails, isLoading } = useGetContractDetails(serviceRequest.contractId!);
  const { isOwn: isProvider, isLoading: isOwnershipLoading } = useIsOwn({
    userId: contractDetails?.serviceRequest.serviceProvider?.id,
    isUserDataLoading: isLoading,
  });

  const { data: reviews, isLoading: isReviewsLoading } = useServiceFeedbackPaginated({
    requestId: serviceRequest.id,
  });

  // Use payment hook to manage all payment-related logic
  const { paymentData, isCreatingPayment, handleOpenPaymentModal, handlePaymentSuccess, stripeModalProps } =
    useContractPayment({
      contractDetails,
      contractId: serviceRequest.contractId,
      serviceRequestId: serviceRequest.id,
      isProvider,
    });

  // Use contract state hook to manage all state calculations
  const { canReview, canProceedToPayment } = useContractState({
    contractDetails,
    paymentData,
    isProvider,
    onContractPaidChange: handleSetIsContractPaid,
  });

  useEffect(() => {
    const debugState = {
      canReview,
      canProceedToPayment,
      isCreatingPayment,
      stripe: {
        clientSecret: stripeModalProps?.clientSecret,
        publishableKey: stripeModalProps?.publishableKey,
      },
      paymentData,
      contractDetails: contractDetails
        ? {
            id: contractDetails.id,
            status: contractDetails.status,
            milestones: contractDetails.milestones,
          }
        : null,
      isProvider,
      reviews: reviews?.length || 0,
    };
  }, [
    canReview,
    canProceedToPayment,
    isCreatingPayment,
    stripeModalProps,
    paymentData,
    contractDetails,
    isProvider,
    reviews,
  ]);

  if (isLoading || !contractDetails) return null;
  const {
    serviceRequest: { requester, serviceProvider },
    milestones,
    paymentConfig,
  } = contractDetails!;

  return (
    <Box sx={styles.container}>
      <Box sx={styles.contractDetailsContainer}>
        <Box sx={styles.userDetailsContainer}>
          <UserDetails user={requester!} title="serviceRequester" />
          <UserDetails user={serviceProvider!} title="serviceProvider" />
        </Box>
        <ContractDetailsCard contractDetails={contractDetails} />
        <ContractServiceDetailsCard contractDetails={contractDetails} />
      </Box>
      <Divider sx={styles.divider} />
      <MilestoneTimeline
        milestones={milestones}
        paymentConfig={paymentConfig}
        isProvider={isProvider}
        isOwnershipLoading={isOwnershipLoading}
        contractId={contractDetails.id}
        contractType={contractDetails.paymentConfig.type}
        contractStatus={contractDetails.status}
        serviceRequestStatus={serviceRequest.status}
        serviceRequestType={serviceRequest.type}
      />
      <ContractActions
        canReview={canReview}
        canProceedToPayment={canProceedToPayment}
        serviceRequest={serviceRequest}
        isProvider={isProvider}
        isCreatingPayment={isCreatingPayment}
        paymentData={paymentData}
        onOpenPaymentModal={handleOpenPaymentModal}
        onPaymentSuccess={handlePaymentSuccess}
        stripeModalProps={stripeModalProps}
      />
      {isReviewsLoading && (
        <>
          <Divider sx={styles.divider} />
          <ServiceFeedbackCardLoader />
        </>
      )}
      {!isReviewsLoading && Boolean(reviews?.length) && reviews?.[0] && (
        <>
          <Divider sx={styles.divider} />
          <ServiceFeedbackCard key={reviews?.[0]?.id} feedback={reviews?.[0]} />
        </>
      )}
    </Box>
  );
};

export default ContractDetails;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "var(--White, #FFF)",
    padding: "16px",
  },
  contractDetailsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  userDetailsContainer: {
    width: "100%",
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    gap: "8px",
  },
  divider: {
    mx: "-16px",
  },
};
